import { FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { ComplianceDocument } from "@/hooks/use-compliance";
import { useUpdateComplianceDocument, useUploadComplianceDocument } from "@/hooks/use-compliance";

interface EditComplianceDialogProps {
  doc: ComplianceDocument | null;
  onClose: () => void;
}

function titleCase(value: string | null): string {
  if (!value) return "--";
  return value
    .split(/[_\s]+/)
    .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
}

function statusVariant(
  status: string | null,
): "success" | "warning" | "destructive" | "secondary" | "default" {
  switch (status) {
    case "active":
    case "compliant":
    case "valid":
    case "current":
      return "success";
    case "pending":
    case "expiring_soon":
      return "warning";
    case "expired":
    case "missing":
    case "non_compliant":
      return "destructive";
    case "not_required":
      return "secondary";
    default:
      return "default";
  }
}

export function EditComplianceDialog({ doc, onClose }: EditComplianceDialogProps) {
  const updateMutation = useUpdateComplianceDocument();
  const uploadMutation = useUploadComplianceDocument();

  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (doc) {
      setIssueDate(doc.issueDate ?? "");
      setExpiryDate(doc.expiryDate ?? "");
      setNotes(doc.notes ?? "");
      setFile(null);
    }
  }, [doc]);

  if (!doc) return null;

  const isPending = updateMutation.isPending || uploadMutation.isPending;

  const handleSave = async () => {
    try {
      if (file && doc.documentTypeId) {
        await uploadMutation.mutateAsync({
          companyId: doc.companyId,
          documentTypeId: doc.documentTypeId,
          file,
          issueDate: issueDate || null,
          expiryDate: expiryDate || null,
          notes: notes || null,
        });
      } else {
        await updateMutation.mutateAsync({
          id: doc.id,
          companyId: doc.companyId,
          issueDate: issueDate || null,
          expiryDate: expiryDate || null,
          notes: notes || null,
        });
      }
      onClose();
    } catch {
      // toasts surfaced by the mutations
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[50vw]">
        <DialogHeader>
          <DialogTitle>Edit Compliance Document</DialogTitle>
          <DialogDescription>
            {titleCase(doc.documentType)}
            {doc.companyName ? ` — ${doc.companyName}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Status</Label>
            <Badge variant={statusVariant(doc.status)}>{titleCase(doc.status)}</Badge>
            <span className="text-xs text-muted-foreground">
              (computed from file + expiry date)
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Issue Date</Label>
              <DatePicker
                value={issueDate || null}
                onChange={(next) => setIssueDate(next ?? "")}
                ariaLabel="Issue date"
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <DatePicker
                value={expiryDate || null}
                onChange={(next) => setExpiryDate(next ?? "")}
                ariaLabel="Expiry date"
                className="w-full"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>{doc.storagePath ? "Replace File" : "Upload File"}</Label>
            {doc.storagePath && doc.fileName ? (
              <a
                href={`/api/compliance-documents/${doc.id}/download`}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <FileText className="h-4 w-4" />
                {doc.fileName}
              </a>
            ) : null}
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {!doc.documentTypeId && (
              <p className="text-xs text-destructive">
                This document has no type assigned — file upload disabled.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
